<template>
    <div>
        <LinkNotFound v-if="linkNotFound" />
        <!-- Every form in this packet is already submitted — dead end, no re-run, URL stays /l/<code>. -->
        <PacketComplete v-else-if="packetComplete" v-bind="packetComplete" />
        <div v-else>
            <ErrorDisplay/>
        </div>
    </div>
</template>

<script>

import { mapState, mapActions } from 'vuex';
import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
import LinkNotFound from '@/components/LinkNotFound/LinkNotFound.vue';
import PacketComplete from '@/pages/Forms/components/PacketComplete.vue';
import { isDeadLinkResponse } from '@/utils/deadLink';
import { buildPacketChain } from '@/pages/Forms/libraries/packet-progress';

// Open Dental's 3-letter language codes -> the frontend codes systemTranslations uses.
// Anything else (including an empty column) is English.
const PATIENT_LANG_MAP = { spa: 'es', rus: 'ru' };

export default {
    name: "UrlRedirect",
    props: ['urlID'],
    components: {
        ErrorDisplay,
        LinkNotFound,
        PacketComplete
    },

    async created() {
        //window.location.href = `https://${this.urlID}`;
        //let origUrl = `http://localhost:8080/view-form/30f7fd47-7f51-4e28-90c8-5ce45e2cb9f1`;
        try {
            // Deliberately UNSCOPED. This response is only used to decide where to route
            // (form vs wiki vs packet, verify-or-not); its `teethNumbers` is discarded.
            // The procedure scope (?aptNum=&codes= from a Get Form QR code) belongs on
            // ViewForm.vue's own getLongUrl call, which is what actually feeds the
            // "Teeth #s" prefill. Passing it here would just make the backend run a
            // procedurelog query whose result is thrown away.
            //
            // The params still reach ViewForm via the generic `{ ...incomingQuery }`
            // spread on every $router.push below — don't remove that.
            let res = await this.getLongUrl({urlID: this.urlID});
            console.dir(res);

            if (isDeadLinkResponse(res)) {
                this.linkNotFound = true;
                return;
            }

            if(res.status === 200) {
                let data = res.data;

                // Forward any incoming query params (e.g. the Tx Presenter wizard's
                // ?txPlan, used for plan-scoped consent completion) through this
                // redirect hop so they survive onto the destination form route.
                // Kept generic — not txPlan-specific. The push's own keys win over
                // these so we never clobber params a branch sets deliberately.
                const incomingQuery = this.$route.query || {};

                // Handle wiki type - redirect to public wiki view
                if (data.type === 'wiki') {
                    console.log(`now sending to public wiki view`);
                    this.$router.push({
                        name: 'PublicWikiView',
                        params: {
                            wikiPageID: data.wikiPageID
                        }
                    });
                    return;
                }

                // "Existing patients" generic link (form OR packet): the patient identifies themself first
                // (DOB + name, then a one-time code). IdentifyPatient then sends them to /l/<patient-specific
                // urlID>, which resolves below like any patient link. Only lang + the incoming query ride
                // along — the packet chain keys are rebuilt by that second resolve (forwarding THIS generic
                // chain's packetNext would leak a stale chain into the patient's run).
                if (data.type !== 'wiki' && data.patNum == null && data.linkMode === 'existing') {
                    const identifyLang = data.type === 'packet'
                        ? (PATIENT_LANG_MAP[data.patientLanguage] || 'en')
                        : (data.patientLanguage === 'spa' ? 'es' : 'en');
                    const identifyQuery = { ...incomingQuery };
                    if (identifyLang !== 'en') identifyQuery.lang = identifyLang;
                    console.log(`now sending to identify-patient (${data.type}, existing-patient link)`);
                    this.$router.push({
                        name: 'IdentifyPatient',
                        params: {
                            urlID: this.urlID,
                            siteID: data.siteID,
                            formName: data.type === 'packet' ? data.packetName : data.formName
                        },
                        query: identifyQuery
                    });
                    return;
                }

                // Pre-identified handoff: IdentifyPatient sets this one-shot marker right before it
                // navigates to /l/<this patient-specific urlID>. Consumed (removed) here, so it counts
                // for exactly ONE resolve; a later open of the same link verifies as usual.
                const identified = (data.patNum !== null && data.patNum !== undefined)
                    ? this.consumeIdentifiedMarker(this.urlID)
                    : false;

                // Handle packet type - redirect to first form with next chain
                if (data.type === 'packet') {
                    console.log(`This is a packet URL. PacketID: ${data.packetID}, Forms: ${data.formUrls.length}`);

                    const patientLang = PATIENT_LANG_MAP[data.patientLanguage] || 'en';

                    // RESUME. buildPacketChain drops every form the backend marked `completed`
                    // (a submission for this patient inside the resume window) and keeps the TRUE
                    // positions of the rest, so 1/2/4-done shows "Section 3", then "Section 5"…
                    // A missing `completed` flag (generic link, or a backend that predates this)
                    // means "not done" — it can only ever show MORE of the packet, never skip a
                    // section the patient has not actually filled.
                    // packetID + patientSpecific let the Action Center group this link-open's
                    // submissions into ONE packet card (see libraries/packet-progress.js).
                    const chain = buildPacketChain(data.formUrls, data.packetName, {
                        packetID: data.packetID,
                        patientSpecific: data.patNum !== null && data.patNum !== undefined
                    });

                    if (chain.allComplete) {
                        // Nothing left to fill. Do NOT push a form: re-rendering a finished packet
                        // re-saves every section and files a duplicate PDF into OD imaging.
                        this.packetComplete = {
                            packetName: data.packetName || '',
                            logoUrl: data.logoUrl || null,
                            siteName: data.siteName || null,
                            sitePhone: data.sitePhone || null,
                            language: patientLang
                        };
                        console.log('Packet: every form already submitted — showing the completed page');
                        return;
                    }

                    // Start at the first INCOMPLETE form. (`chain.first` is null only for a packet
                    // with no forms at all — fall back to today's behaviour rather than 500-ing.)
                    const firstForm = chain.first || data.formUrls[0];

                    // Build query params for the first form: packetNext/packetSteps/packetTotal/
                    // packetStep/packetName all come from the chain.
                    let query = { ...chain.query };
                    query.packetVerified = '0'; // Will be set to '1' after verification
                    if (patientLang !== 'en') query.lang = patientLang;

                    if (data.patNum === null || data.skipValidation === true || identified) {
                        // No verification needed (generic, skipValidation, or just identified) - go directly to first form
                        query.packetVerified = '1';
                        console.log(`Packet: going directly to first form (no verification needed)`);
                        this.$router.push({
                            name: 'ViewFormWithUrlID',
                            params: {
                                formUrlID: firstForm.formUrlID,
                                urlID: firstForm.urlID
                            },
                            query: { ...incomingQuery, ...query }
                        });
                    } else {
                        // Verification required - go to verify form first
                        console.log(`Packet: going to verify form first`);
                        const verifyPacketQuery = {};
                        ['packetTotal', 'packetStep', 'packetSteps', 'packetName', 'packetID', 'packetRun', 'packetPatient'].forEach(key => {
                            if (chain.query[key] !== undefined) verifyPacketQuery[key] = chain.query[key];
                        });
                        this.$router.push({
                            name: 'VerifyForm',
                            params: {
                                formUrlID: firstForm.formUrlID,
                                urlID: firstForm.urlID,
                                siteID: data.siteID,
                                formName: data.packetName,
                                patientLanguage: patientLang,
                                // Pass packet info through to VerifyForm so it can forward to ViewForm
                                packetNext: chain.query.packetNext || ''
                            },
                            // The rest of the packet keys ride in the QUERY: VerifyForm already
                            // spreads `{ ...this.$route.query }` onto its own push, so they reach
                            // ViewForm with NO change to VerifyForm. packetNext stays a param
                            // (VerifyForm re-adds it) and packetVerified is VerifyForm's to set.
                            query: { ...incomingQuery, ...verifyPacketQuery }
                        });
                    }
                    return;
                }

                // Handle form type (existing behavior)
                //window.window.location.href = data.OrigUrl

                // Determine language: convert Open Dental 'spa' to 'es' for frontend
                const patientLang = data.patientLanguage === 'spa' ? 'es' : 'en';
                console.log(`Patient language preference: ${data.patientLanguage} -> ${patientLang}`);

                if(data.patNum === null) {
                    // No patient associated - go directly to form
                    console.log(`now sending to viewform (no patient associated)`);
                    this.$router.push({
                        name: 'ViewFormWithUrlID',
                        params: {
                            formUrlID: data.formUrlID,
                            urlID: this.urlID  // Pass the nanoid for getLongUrl calls
                        },
                        query: { ...incomingQuery }
                    });
                } else if (data.skipValidation === true || identified) {
                    // Patient-specific form but validation is skipped (or the patient just identified
                    // themself on IdentifyPatient) - go directly to form
                    console.log(`now sending to viewform (skipValidation enabled, lang: ${patientLang})`);
                    this.$router.push({
                        name: 'ViewFormWithUrlID',
                        params: {
                            formUrlID: data.formUrlID,
                            urlID: this.urlID  // Pass the nanoid for getLongUrl calls
                        },
                        query: patientLang === 'es' ? { ...incomingQuery, lang: 'es' } : { ...incomingQuery }  // Add ?lang=es for Spanish patients
                    });
                } else {
                    // Patient-specific form with validation required - verify first
                    console.log(`now sending to verifyform (lang: ${patientLang})`);
                    //console.log(this.$router);

                    this.$router.push({
                        name: 'VerifyForm',
                        params: {
                            formUrlID: data.formUrlID,
                            urlID: this.urlID,  // Pass the original 7-char nanoid for verification
                            siteID: data.siteID,
                            formName: data.formName,
                            patientLanguage: patientLang  // Pass patient language to VerifyForm
                        },
                        query: { ...incomingQuery }
                    });
                }
            }

        } catch(err) {
            console.error(err);
        }
    },

    data: () => ({
        linkNotFound: false,
        // Set (instead of pushing a form) when every section of a packet is already submitted.
        // Shape: { packetName, logoUrl, siteName, sitePhone, language } — v-bind'd onto PacketComplete.
        packetComplete: null,
    }),

    methods: {
            ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),
            ...mapActions('urlredirect', ['getLongUrl']),

            // Read AND remove IdentifyPatient's one-shot marker. Storage can throw (private mode,
            // blocked site data) — a throw just means "not identified", i.e. the normal verify path.
            consumeIdentifiedMarker(urlID) {
                const key = 'teamio_identified_' + urlID;
                try {
                    if (sessionStorage.getItem(key) === '1') {
                        sessionStorage.removeItem(key);
                        return true;
                    }
                } catch (e) {
                    // fall through
                }
                return false;
            },
    },

    computed: {            
        ...mapState({
            loading: state => state.alert.loading,
            loaded: state => state.alert.loaded,
            errored: state => state.alert.errored,
            alertMessage: state => state.alert.message,                
        })
    }



}

</script>